<template>
  <component :is="decoratorName">
    <a-form-item>
      <AvatarViewer class="header"
                    :width='100'
                    :height='100'
                    :radius='50'
                    :show-upload='false'
                    :img='avatar'
                    :nickname='nickname' />
    </a-form-item>
    <a-form v-if="mode === 'bind'"
            class="form"
            :model="bindState"
            name="basic"
            :wrapper-col="{ span: 24 }"
            autocomplete="off"
            layout="vertical"
            @finish="onBindFinish"
            @finish-failed="onBindFinishFailed">
      <a-form-item name="user"
                   :rules="[{ required: true, message: $t('oauth2_implements.CreateOne.enterUsername') }]">
        <a-input v-model:value="bindState.user"
                 :placeholder="$t('oauth2_implements.CreateOne.username')"
                 style="width:100%"
                 autocomplete="off"
                 size='large'>
          <template #prefix>
            <user-outlined />
          </template>
        </a-input>
      </a-form-item>
      <a-form-item name="password"
                   :rules="[{ required: true, message: $t('oauth2_implements.CreateOne.enterPassword') }]">
        <a-input-password v-model:value="bindState.password"
                          :placeholder="$t('oauth2_implements.CreateOne.password')"
                          autocomplete="off"
                          size='large'>
          <template #prefix>
            <lock-outlined />
          </template>
        </a-input-password>
      </a-form-item>
      <a-row class="footer">
        <a-button type="primary"
                  html-type="submit"
                  size="large">{{ $t('oauth2_implements.CreateOne.bindUser') }}</a-button>
        <a type="primary"
           size="large"
           @click="() => { switchMode('newUser') }">{{ $t('oauth2_implements.CreateOne.register') }}</a>
      </a-row>
    </a-form>
    <a-form v-if="mode === 'newUser'"
            ref="newUserForm"
            class="form"
            :model="newUserState"
            name="basic"
            :wrapper-col="{ span: 24 }"
            autocomplete="off"
            layout="vertical"
            @finish="onNewUserFinish"
            @finish-failed="onNewUserFinishFailed">
      <a-form-item name="mobile"
                   :rules="[{ required: true, validator: validateMobile }]">
        <a-input v-model:value="newUserState.mobile"
                 :placeholder="$t('oauth2_implements.CreateOne.mobileNumber')"
                 style="width:100%"
                 autocomplete="off"
                 size='large'>
          <template #prefix>
            <user-outlined />
          </template>
        </a-input>
      </a-form-item>
      <a-row :gutter="10">
        <a-col :span="16">
          <a-form-item name="captcha"
                       :rules="[{ required: true, message: $t('oauth2_implements.CreateOne.enterCode') }]">
            <a-input v-model:value="newUserState.captcha"
                     autocomplete="off"
                     :placeholder="$t('oauth2_implements.CreateOne.enterCode')"
                     size='large'>
              <template #prefix>
                <code-outlined />
              </template>
            </a-input>
          </a-form-item>
        </a-col>
        <a-col :span="8"
               class="captcha">
          <count-down-button html-type="submit"
                             :text="$t('oauth2_implements.CreateOne.getCode')"
                             :counting-text-post="$t('oauth2_implements.CreateOne.retryLater')"
                             :disabled="!newUserState.isMobileValid"
                             size='large'
                             @click="getCaptcha"></count-down-button>
        </a-col>
      </a-row>
      <a-row class="footer">
        <a-button type="primary"
                  html-type="submit"
                  size="large">{{ $t('oauth2_implements.CreateOne.register') }}</a-button>
        <a type="primary"
           size="large"
           @click="() => { switchMode('bind') }">{{ $t('oauth2_implements.CreateOne.bindUser') }}</a>
      </a-row>
    </a-form>
  </component>
</template>

<script setup lang="ts">
import { CORE_AXIOS, Http } from '@/core'
import type { LoginPageOption, LoginPageService, Plugin } from '@/plugins'
import { PLUGIN_AUTH } from '@/plugins/builtin/BuiltinPluginSymbol'
import { AvatarViewer, CountDownButton } from '@iota-cn/ui-components'
import { Validator } from '@iota-cn/util-validation'
import { message } from 'ant-design-vue'
import type { Rule } from 'ant-design-vue/lib/form'
import Cookie from 'js-cookie'
import { container } from 'tsyringe'
import { reactive, ref } from 'vue'
import { useOauth2 } from '../Oauth2Plugin'
// eslint-disable-next-line no-unused-vars
import ContainerPage from './ContainerPage.vue'
import { useI18n } from "vue-i18n"
const { t } = useI18n()
const axios = container.resolve<Http>(CORE_AXIOS)
const auth = container.resolve<Plugin<LoginPageOption, LoginPageService>>(PLUGIN_AUTH)

const b64DecodeUnicode = (str: string) => {
  if (!str) {
    return
  }
  return decodeURIComponent(atob(str).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
  }).join(''))
}

const oauth2State = useOauth2()
const { providers, decoratorComponentName } = oauth2State.$state
const { wechat } = providers
const avatar = Cookie.get(providers.wechat.avatarCookieKey)
const nickname = b64DecodeUnicode(Cookie.get(wechat.usernameCookieKey) as string)

const decoratorName = decoratorComponentName || 'container-page'

const bindState = reactive({
  user: '',
  password: '',
})

const newUserState = reactive({
  mobile: '',
  captcha: '',
  isMobileValid: false
})

const mode = ref<string>('newUser')

const switchMode = (m: string) => {
  mode.value = m
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onBindFinish = (values: any) => {
  values.username = values.user
  values.email = values.user
  auth.service.signin('v1/api/oauth2/third_parties/wechat/signin', values, t('oauth2_implements.CreateOne.registeSuccess'))
}

const onBindFinishFailed = (errorInfo: string) => {
  console.log('Failed:', errorInfo)
}

const getCaptcha = async () => {
  const mobile = newUserState.mobile
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios.post(auth.options.api?.fetchSmsValidationCode || '/v1/api/validations/smses', { mobile }).then((res: any) => {
    if (res.status === 403) {
      message.error(t('oauth2_implements.CreateOne.errMsg1'))
    }
  })
}

const newUserForm = ref()
const onNewUserFinish = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newUser = {
    mobile: newUserState.mobile,
    username: newUserState.mobile,
    password: newUserState.mobile,
    captcha: newUserState.captcha
  }
  axios.post('/v1/api/signup', newUser).then(() => {
    auth.service.signin('v1/api/oauth2/third_parties/wechat/signin', {
      mobile: newUserState.mobile,
      username: newUserState.mobile,
      password: newUserState.mobile,
      email: newUserState.mobile
    }, "注册成功")
  }).catch(() => {
    message.error(t('oauth2_implements.CreateOne.errMsg2'))
  })
}

const onNewUserFinishFailed = () => {

}

const validateMobile = async (_rule: Rule, value: string) => {
  if (Validator.isPhone(value)) {
    return axios.get(`/v1/api/validations/mobiles/validate?mobile=${value}`)
      .then(() => {
        newUserState.isMobileValid = true
        return Promise.resolve()
      }).catch(() => {
        newUserState.isMobileValid = false
        return Promise.reject(t('oauth2_implements.CreateOne.enterNumber'))
      })
  } else {
    newUserState.isMobileValid = false
    return Promise.reject(t('oauth2_implements.CreateOne.enterCorrectNumber'))
  }
}
</script>

<style scoped lang="less">
.header {
  margin: 0 auto;
}

.form {
  width: 100%;

  .oauth2 {
    margin-top: 10px;
    display: flex;
    justify-content: start;
    align-items: center;

    .item {
      font-size: 24px;
      margin: 0 20px 0 20px;
    }
  }

  .captcha {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;

    button {
      width: 100%
    }
  }

  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    button {
      width: 50%;
    }
  }
}
</style>
